import React, { useState, useEffect } from "react";
import { api } from "../api";
import Icon from "./Icon";
import { useAssetStore } from "../store/useAssetStore";

export default function DemoConsoleModal({ isOpen, onClose }) {
  const [targets, setTargets] = useState([]);
  const [selectedHostname, setSelectedHostname] = useState("");
  const [loadingAction, setLoadingAction] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const fetchAllData = useAssetStore((state) => state.fetchAllData);

  const handleClose = () => {
    setFeedbackMessage(null);
    if (onClose) onClose();
  };

  useEffect(() => {
    setFeedbackMessage(null);
    if (isOpen) {
      api("/demo/targets")
        .then((data) => {
          setTargets(data || []);
          if (data && data.length > 0) {
            setSelectedHostname(data[0].hostname);
          }
        })
        .catch(() => {
          setTargets([]);
        });
    } else {
      setFeedbackMessage(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedDevice =
    targets.find((t) => t.hostname === selectedHostname) || targets[0];
  const isNetworkDevice =
    selectedDevice &&
    ["router", "switch", "firewall", "gateway", "load_balancer"].includes(
      (selectedDevice.asset_category || "").toLowerCase(),
    );

  const handleTrigger = async (actionType, actionLabel) => {
    if (!selectedHostname) return;
    setLoadingAction(actionType);
    setFeedbackMessage(null);
    try {
      const res = await api("/demo/trigger", {
        method: "POST",
        body: JSON.stringify({
          hostname: selectedHostname,
          action: actionType,
        }),
      });
      setFeedbackMessage({
        type: "success",
        text: `Fault Injection '${actionLabel}' executed for ${selectedHostname}.`,
      });

      if (res && res.hostname) {
        useAssetStore.getState().updateLiveTelemetry(res.hostname, {
          status: res.device_status,
          specs: res.specs || {},
          health_score: res.specs?.health_score,
          ping_latency_ms: res.specs?.ping_latency_ms,
        });
      }

      // Force real-time synchronization across all stores & views
      await fetchAllData(true);
    } catch (err) {
      setFeedbackMessage({
        type: "error",
        text:
          err?.message || `Failed to execute Fault Injection '${actionLabel}'.`,
      });
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 620,
          background: "#FFFFFF",
          borderRadius: 12,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          border: "1px solid #E2E8F0",
          overflow: "hidden",
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#F8FAFC",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: "#E0E7FF",
                color: "#4338CA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon name="sliders" size={18} />
            </div>
            <div>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#0F172A",
                  margin: 0,
                }}
              >
                Infrastructure Resilience & Fault Injector Console
              </h3>
              <p style={{ fontSize: 12, color: "#64748B", margin: 0 }}>
                Inject hardware fault scenarios and WAN latency to validate
                real-time AIOps telemetry response.
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              color: "#64748B",
              padding: 4,
              borderRadius: 6,
            }}
          >
            <Icon name="x" size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div
          style={{
            padding: 24,
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* Target Infrastructure Selector */}
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#475569",
                display: "block",
                marginBottom: 6,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Target Infrastructure Appliance
            </label>
            <select
              value={selectedHostname}
              onChange={(e) => setSelectedHostname(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px",
                borderRadius: 8,
                border: "1px solid #CBD5E1",
                fontSize: 14,
                fontWeight: 600,
                color: "#0F172A",
                background: "#FFFFFF",
                outline: "none",
              }}
            >
              {targets.length === 0 ? (
                <option value="">
                  No candidate infrastructure devices found
                </option>
              ) : (
                targets.map((t) => (
                  <option key={t.hostname} value={t.hostname}>
                    {t.name} ({t.ip_address || t.hostname}) — [
                    {(t.asset_category || "server").toUpperCase()}] (Status:{" "}
                    {(t.status || "active").toUpperCase()})
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Fault Injection Actions Grid (Dynamic for Router vs Server) */}
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#475569",
                display: "block",
                marginBottom: 10,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              {isNetworkDevice
                ? "Router & Network Fault Injection Controls"
                : "Server & Compute Fault Injection Controls"}
            </label>

            {isNetworkDevice ? (
              /* Network Device / Router Controls */
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 12,
                }}
              >
                {/* High Load / CPU Spike */}
                <button
                  disabled={loadingAction !== null || !selectedHostname}
                  onClick={() =>
                    handleTrigger(
                      "cpu_spike",
                      "Inject High Load / CPU Spike (85%)",
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 8,
                    border: "1px solid #FED7AA",
                    background: "#FFF7ED",
                    color: "#C2410C",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon name="cpu" size={20} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      Inject High Load / CPU Spike (85%)
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#EA580C",
                        marginTop: 2,
                      }}
                    >
                      Increases load utilization while keeping router Active &
                      Monitored
                    </div>
                  </div>
                </button>

                {/* Router Outage */}
                <button
                  disabled={loadingAction !== null || !selectedHostname}
                  onClick={() =>
                    handleTrigger(
                      "router_down",
                      "Inject Router Interface Outage",
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 8,
                    border: "1px solid #FCA5A5",
                    background: "#7F1D1D",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon name="alert-triangle" size={20} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      Inject Router Outage (Offline)
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#FCA5A5",
                        marginTop: 2,
                      }}
                    >
                      Simulates interface outage (Marks appliance Inactive)
                    </div>
                  </div>
                </button>

                {/* Router Latency Spike */}
                <button
                  disabled={loadingAction !== null || !selectedHostname}
                  onClick={() =>
                    handleTrigger(
                      "latency_spike",
                      "Inject Response Latency (185ms)",
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 8,
                    border: "1px solid #FDE68A",
                    background: "#FFFBEB",
                    color: "#92400E",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon name="clock" size={20} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      Inject WAN Latency (185ms)
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#B45309",
                        marginTop: 2,
                      }}
                    >
                      Simulates network congestion & response latency
                      degradation
                    </div>
                  </div>
                </button>

                {/* Reset Healthy Router */}
                <button
                  disabled={loadingAction !== null || !selectedHostname}
                  onClick={() =>
                    handleTrigger(
                      "reset_healthy",
                      "Restore Router Health (100%)",
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 8,
                    border: "1px solid #A7F3D0",
                    background: "#ECFDF5",
                    color: "#065F46",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon name="check-circle" size={20} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      Restore Appliance Health (100%)
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#047857",
                        marginTop: 2,
                      }}
                    >
                      Restores active link status & healthy SNMP reachability
                    </div>
                  </div>
                </button>
              </div>
            ) : (
              /* Server / Compute Controls */
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: 12,
                }}
              >
                {/* CPU Workload Spike */}
                <button
                  disabled={loadingAction !== null || !selectedHostname}
                  onClick={() =>
                    handleTrigger(
                      "cpu_spike",
                      "Inject Compute CPU Workload (85%)",
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 8,
                    border: "1px solid #FECACA",
                    background: "#FEF2F2",
                    color: "#991B1B",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon name="zap" size={20} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      Inject Compute CPU Load (85%)
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#B91C1C",
                        marginTop: 2,
                      }}
                    >
                      Generates heavy compute CPU load on target infrastructure
                      host
                    </div>
                  </div>
                </button>

                {/* Reset Healthy Server */}
                <button
                  disabled={loadingAction !== null || !selectedHostname}
                  onClick={() =>
                    handleTrigger(
                      "reset_healthy",
                      "Restore Server Health (100%)",
                    )
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 16px",
                    borderRadius: 8,
                    border: "1px solid #A7F3D0",
                    background: "#ECFDF5",
                    color: "#065F46",
                    fontWeight: 600,
                    fontSize: 13,
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                >
                  <Icon name="check-circle" size={20} />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>
                      Restore Host Health (100%)
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 400,
                        color: "#047857",
                        marginTop: 2,
                      }}
                    >
                      Terminates stress tasks & restores normal compute
                      utilization
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Feedback Alert Message */}
          {feedbackMessage && (
            <div
              style={{
                padding: "10px 14px",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                background:
                  feedbackMessage.type === "success" ? "#F0FDF4" : "#FEF2F2",
                color:
                  feedbackMessage.type === "success" ? "#166534" : "#991B1B",
                border: `1px solid ${
                  feedbackMessage.type === "success" ? "#BBF7D0" : "#FECACA"
                }`,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Icon
                name={
                  feedbackMessage.type === "success" ? "check" : "alert-circle"
                }
                size={16}
              />
              <span>{feedbackMessage.text}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
